import { BroadcastChannelManager } from './BroadcastChannelManager';
import { BroadcastCounterManager } from './BroadcastCounterManager';
import type { IBroadcastChannelManagerOptions } from './BroadcastChannelManager.types';

/**
 * Singleton registry that lazily creates and caches broadcast channel
 * instances by name. Supports both generic typed channels and
 * number-specific counter channels via a shared internal map.
 *
 * @example
 * const counter = broadcastRegistry.getCounter('store-refresh', 'store-refresh-count');
 * counter.increment();
 *
 * const settings = broadcastRegistry.getChannel<ISettings>('user-settings', {
 *   storageKey: 'user-settings',
 *   initialValue: { theme: 'dark' },
 *   serialize: (v) => JSON.stringify(v),
 *   deserialize: (raw) => JSON.parse(raw),
 * });
 * settings.setValue({ theme: 'light' });
 */
class BroadcastManagerRegistry {
  private channels = new Map<string, BroadcastChannelManager<unknown>>();

  getChannel<T>(
    channelName: string,
    options: Omit<IBroadcastChannelManagerOptions<T>, 'channelName'>,
  ): BroadcastChannelManager<T> {
    const existing = this.channels.get(channelName);
    if (existing) {
      return existing as BroadcastChannelManager<T>;
    }
    const manager = new BroadcastChannelManager<T>({ channelName, ...options });
    this.channels.set(channelName, manager as BroadcastChannelManager<unknown>);
    return manager;
  }

  getCounter(channelName: string, storageKey: string): BroadcastCounterManager {
    const existing = this.channels.get(channelName);
    if (existing) {
      return existing as BroadcastCounterManager;
    }
    const manager = new BroadcastCounterManager({ channelName, storageKey });
    this.channels.set(channelName, manager as BroadcastChannelManager<unknown>);
    return manager;
  }

  resetCounter(channelName: string): void {
    const existing = this.channels.get(channelName);
    if (existing instanceof BroadcastCounterManager) {
      existing.reset();
    }
  }

  destroy(channelName: string): void {
    const manager = this.channels.get(channelName);
    if (manager) {
      manager.destroy();
      this.channels.delete(channelName);
    }
  }

  destroyAll(): void {
    this.channels.forEach((m) => m.destroy());
    this.channels.clear();
  }
}

export const broadcastRegistry = new BroadcastManagerRegistry();
